import type { Aggregates } from 'hooks/useSpaceSearch';
import { FC, useState } from 'react';
import { useTranslation } from 'utils/i18n';
// constants
import { distances } from '../../constants/distances';
import type { SearchContextValue } from '../../state/search';
import type { SearchFilters } from '../../state/search/reducer';
import CheckBoxList from '../fields/checkBoxList';
import FilterDropdown from '../filterDropdown';
import IncrementalInput from '../incrementalInput';
import ListItem from '../listItem';

interface FilterAggregate {
  name: string;
  count: number;
}

interface Props {
  aggregates?: Aggregates;
  filters: SearchFilters;
  actions: SearchContextValue['actions'];
  updateCurrentPage?: (pageNumber: number) => void;
}

const NestedFilterDropdown: FC<Props> = ({
  aggregates = { property: [], facets: [] },
  filters,
  updateCurrentPage,
  actions,
}) => {
  const { t } = useTranslation('common');
  const [distance, setDistance] = useState(filters.distance);
  const [capacity, setCapacity] = useState(filters.capacity);
  const [properties, setProperty] = useState(filters.property);
  const [facets, setFacets] = useState(filters.facets);
  const [isOpen, setIsOpen] = useState(!!distance);

  const onSelectDistance = (value: number) => {
    setIsOpen(false);
    setDistance(value);
    actions.updateDistance(value);
  };

  return (
    <FilterDropdown
      label="Filters"
      name="filters"
      hideSelectAllButton
      handleApply={() => {
        updateCurrentPage?.(1);
        actions.updateProperty(properties);
        actions.updateDistance(distance);
        actions.updateCapacity(capacity);
        actions.updateFacets(facets);
      }}
      handleClearAll={() => {
        updateCurrentPage?.(1);
        setProperty([]);
        setCapacity(null);
        setCapacity(null);
        setFacets([]);
        actions.updateProperty([]);
        actions.updateDistance(null);
        actions.updateCapacity(null);
        actions.updateFacets([]);
      }}
      selectedItems={[
        filters.distance ? filters.distance : null,
        filters.capacity ? filters.capacity : null,
        filters.property ? filters.property[0] : null,
        filters.facets ? filters.facets[0] : null,
      ]}
      expandable
      showDropdownArrow={false}
      alwaysShowBadgeCount
      clearAllText={t('clear_all')}
      testId="mobile-filters"
      isOpen={isOpen}
      setIsOpen={setIsOpen}
    >
      {aggregates && (
        <FilterDropdown
          label="Property"
          name="property"
          hideSelectAllButton
          handleApply={() => {
            actions.updateProperty(properties);
          }}
          handleClearAll={() => {
            setProperty([]);
            actions.updateProperty([]);
          }}
          displayButtons={false}
          positionRelative
          testId="property"
        >
          <CheckBoxList
            id="property"
            name="property[]"
            options={(aggregates.property ?? []).map((i: FilterAggregate) => {
              return {
                name: `${i.name} (${i.count})`,
                value: i.name,
                checked: properties.includes(i.name),
              };
            })}
            onChange={e => {
              const { value } = e.currentTarget;

              const values = properties.includes(value)
                ? properties.filter((i: string) => i !== value)
                : [...properties, value];

              setProperty(values);
            }}
          />
        </FilterDropdown>
      )}

      {filters.location && (
        <FilterDropdown
          hideSelectAllButton
          label="Distance"
          name="distance"
          handleApply={() => {
            actions.updateDistance(distance);
          }}
          handleClearAll={() => {
            setDistance(null);
            actions.updateDistance(null);
          }}
          displayButtons={false}
          positionRelative
          testId="distance"
        >
          <ul>
            {Object.keys(distances).map(label => (
              <ListItem
                key={
                  distances[label as keyof typeof distances] === null
                    ? 1
                    : distances[label as keyof typeof distances] * 1000
                }
                text={label}
                value={
                  distances[label as keyof typeof distances] === null
                    ? ''
                    : distances[label as keyof typeof distances]
                }
                selected={
                  distance === distances[label as keyof typeof distances]
                }
                onClick={() => {
                  onSelectDistance(distances[label as keyof typeof distances]);
                }}
              />
            ))}
          </ul>
        </FilterDropdown>
      )}

      <FilterDropdown
        label="Capacity"
        name="capacity"
        hideSelectAllButton
        clearAllText={t('reset')}
        canApply={!!capacity && capacity >= 1}
        handleApply={() => {
          setCapacity(capacity);
          actions.updateCapacity(capacity);
        }}
        handleClearAll={() => {
          setCapacity(null);
          actions.updateCapacity(null);
        }}
        hideApplyButton
        testId="capacity"
      >
        <IncrementalInput
          id="capacity"
          label="Guests:"
          initialValue={filters.capacity === null ? 0 : filters.capacity}
          minValue={1}
          onChange={setCapacity}
        />
      </FilterDropdown>
      {aggregates.facets && (
        <FilterDropdown
          label="Features"
          name="facets"
          handleApply={() => {
            setFacets(facets);
            actions.updateFacets(facets);
          }}
          displayButtons={false}
          positionRelative
          testId="facets"
          hideSelectAllButton
        >
          <CheckBoxList
            id="facet"
            name="facets[]"
            options={aggregates.facets.map((i: FilterAggregate) => {
              return {
                name: `${i.name} (${i.count})`,
                value: i.name,
                checked: facets.includes(i.name),
              };
            })}
            onChange={e => {
              const { value } = e.currentTarget;

              const values = facets.includes(value)
                ? facets.filter((i: string) => i !== value)
                : [...facets, value];

              setFacets(values);
            }}
          />
        </FilterDropdown>
      )}
    </FilterDropdown>
  );
};

export default NestedFilterDropdown;
